import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EmailBodyEditor from './EmailBodyEditor';
import SendConfirmModal from './SendConfirmModal';
import { marketingApi, type Campaign, type CampaignInput, type MarketingOverview } from '../marketingApi';

interface Props {
  campaignId: number | null; // null = new
  overview: MarketingOverview;
  onBack: () => void;
  onChanged: () => void; // refresh list / overview
}

const EMPTY: CampaignInput = {
  name: '',
  subject: '',
  preheader: '',
  body_html: '',
  cta_text: '',
  cta_url: '',
};

const pick = (c: Campaign): CampaignInput => ({
  name: c.name,
  subject: c.subject,
  preheader: c.preheader,
  body_html: c.body_html,
  cta_text: c.cta_text,
  cta_url: c.cta_url,
});

function formatCountdown(ms: number) {
  if (ms <= 0) return '0:00';
  const total = Math.floor(ms / 1000);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
}

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <label className="block">
    <span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>
    {children}
    {hint && <span className="block text-xs text-gray-500 mt-1">{hint}</span>}
  </label>
);

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-500';

const CampaignEditor: React.FC<Props> = ({ campaignId, overview, onBack, onChanged }) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [form, setForm] = useState<CampaignInput>(EMPTY);
  const [saved, setSaved] = useState<CampaignInput>(EMPTY);
  const [loading, setLoading] = useState(campaignId !== null);
  const [busy, setBusy] = useState<null | 'save' | 'test' | 'cancel'>(null);
  const [notice, setNotice] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewMode, setPreviewMode] = useState<'desktop' | 'mobile'>('desktop');
  const [showConfirm, setShowConfirm] = useState(false);
  // Where test emails go. Remembered per browser; defaults to the admin account email.
  const [testEmail, setTestEmail] = useState<string>(() => {
    try {
      return localStorage.getItem('marketing_test_email') || overview.admin_email || '';
    } catch {
      return overview.admin_email || '';
    }
  });
  const testEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail.trim());
  const [now, setNow] = useState(Date.now());

  const isDraft = !campaign || campaign.status === 'draft';
  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(saved), [form, saved]);

  // ---------------------------------------------------------------- load
  useEffect(() => {
    if (campaignId === null) return;
    let alive = true;
    marketingApi
      .get(campaignId)
      .then((c) => {
        if (!alive) return;
        setCampaign(c);
        setForm(pick(c));
        setSaved(pick(c));
      })
      .catch((err) => setNotice({ kind: 'error', text: err.message }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [campaignId]);

  // ---------------------------------------------------------------- live preview (same renderer as the real send)
  const previewReq = useRef(0);
  useEffect(() => {
    const id = ++previewReq.current;
    const t = window.setTimeout(() => {
      marketingApi
        .preview(form)
        .then(({ html }) => {
          if (id === previewReq.current) setPreviewHtml(html);
        })
        .catch(() => undefined);
    }, 500);
    return () => window.clearTimeout(t);
  }, [form]);

  // ---------------------------------------------------------------- countdown while scheduled
  useEffect(() => {
    if (campaign?.status !== 'scheduled') return;
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [campaign?.status]);

  const msLeft = campaign?.scheduled_for ? new Date(campaign.scheduled_for).getTime() - now : 0;
  const scheduledButPassed = campaign?.status === 'scheduled' && msLeft <= 0;

  // ---------------------------------------------------------------- unsaved-changes guard
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const goBack = () => {
    if (dirty && !window.confirm('You have unsaved changes. Leave without saving?')) return;
    onBack();
  };

  const set = <K extends keyof CampaignInput>(key: K, value: CampaignInput[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // ---------------------------------------------------------------- actions
  const save = useCallback(async (): Promise<Campaign | null> => {
    if (!form.name.trim()) {
      setNotice({ kind: 'error', text: 'Give the campaign an internal name first.' });
      return null;
    }
    setBusy('save');
    setNotice(null);
    try {
      const result = campaign ? await marketingApi.update(campaign.id, form) : await marketingApi.create(form);
      setCampaign(result);
      // Server sanitizes the body; keep editor in sync with what is stored
      setForm(pick(result));
      setSaved(pick(result));
      onChanged();
      return result;
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof Error ? err.message : 'Save failed' });
      return null;
    } finally {
      setBusy(null);
    }
  }, [campaign, form, onChanged]);

  const sendTest = async () => {
    let target = campaign;
    if (!target || dirty) {
      target = await save();
      if (!target) return;
    }
    setBusy('test');
    setNotice(null);
    try {
      const to = testEmail.trim();
      try {
        localStorage.setItem('marketing_test_email', to);
      } catch {
        /* storage blocked: fine, just not remembered */
      }
      const result = await marketingApi.sendTest(target.id, to);
      setCampaign(result);
      setNotice({ kind: 'ok', text: `Test email sent to ${result.tested_to}. Check your inbox (and spam folder).` });
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof Error ? err.message : 'Test send failed' });
    } finally {
      setBusy(null);
    }
  };

  const confirmSend = async (typedSubject: string) => {
    if (!campaign) return;
    const result = await marketingApi.send(campaign.id, typedSubject, campaign.content_hash);
    setCampaign(result);
    setShowConfirm(false);
    setNotice({ kind: 'ok', text: 'Scheduled. You can still cancel during the countdown.' });
    onChanged();
  };

  const cancelSend = async () => {
    if (!campaign) return;
    if (!window.confirm('Cancel this send? The campaign goes back to draft and nobody receives it.')) return;
    setBusy('cancel');
    setNotice(null);
    try {
      const result = await marketingApi.cancel(campaign.id);
      setCampaign(result);
      setNotice({ kind: 'ok', text: 'Send cancelled. The campaign is a draft again.' });
      onChanged();
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof Error ? err.message : 'Cancel failed' });
    } finally {
      setBusy(null);
    }
  };

  // ---------------------------------------------------------------- send gate
  const blockers: string[] = [];
  if (!overview.configured) blockers.push('Marketing email is not configured on the server');
  if (overview.subscriber_count === 0) blockers.push('No subscribers yet');
  if (!campaign) blockers.push('Save the draft');
  else if (dirty) blockers.push('Save your latest changes');
  else if (!campaign.is_tested) blockers.push('Send a test email of this version');
  const canSend = isDraft && blockers.length === 0;

  if (loading) {
    return <div className="py-20 text-center text-gray-500">Loading…</div>;
  }

  return (
    <div>
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <button onClick={goBack} className="flex items-center text-sm text-gray-600 hover:text-gray-900 font-medium">
          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          All campaigns
        </button>
        <span className="text-sm text-gray-500">
          {dirty ? 'Unsaved changes' : campaign ? 'All changes saved' : 'New campaign'}
        </span>
      </div>

      {/* Status banners */}
      {campaign?.status === 'scheduled' && !scheduledButPassed && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
          <div className="text-sm text-amber-900">
            <p className="font-semibold">
              Sending to {campaign.recipient_count} subscribers in {formatCountdown(msLeft)}
            </p>
            <p>Scheduled by {campaign.sent_by_username}. Cancel now if anything is wrong.</p>
          </div>
          <button
            onClick={cancelSend}
            disabled={busy !== null}
            className="px-4 py-2 text-sm font-semibold text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50"
          >
            {busy === 'cancel' ? 'Cancelling…' : 'Cancel send'}
          </button>
        </div>
      )}
      {(campaign?.status === 'sent' || scheduledButPassed) && (
        <div className="mb-6 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-900">
          Sent to {campaign?.recipient_count} subscribers
          {campaign?.scheduled_for && ` on ${new Date(campaign.scheduled_for).toLocaleString()}`}. Delivery stats are in
          the Resend dashboard under Broadcasts. Use <em>Duplicate</em> from the list to reuse this email.
        </div>
      )}

      {notice && (
        <div
          className={`mb-6 rounded-lg px-4 py-3 text-sm border ${
            notice.kind === 'ok' ? 'bg-blue-50 border-blue-200 text-blue-900' : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          {notice.text}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* ------------------------------------------------ Compose */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-5">
          <h2 className="text-lg font-semibold text-gray-900">Compose</h2>

          <Field label="Campaign name" hint="Only admins see this.">
            <input className={inputCls} disabled={!isDraft} value={form.name} maxLength={200}
              onChange={(e) => set('name', e.target.value)} placeholder="e.g. SDG Week 2026 invite" />
          </Field>

          <Field label="Subject line">
            <input className={inputCls} disabled={!isDraft} value={form.subject} maxLength={200}
              onChange={(e) => set('subject', e.target.value)} placeholder="What recipients see in their inbox" />
          </Field>

          <Field label="Preview text (optional)" hint="Shown next to the subject in most inboxes.">
            <input className={inputCls} disabled={!isDraft} value={form.preheader} maxLength={200}
              onChange={(e) => set('preheader', e.target.value)} placeholder="A one-line teaser" />
          </Field>

          <div>
            <span className="block text-sm font-medium text-gray-700 mb-1">Body</span>
            <EmailBodyEditor value={form.body_html} onChange={(html) => set('body_html', html)} readOnly={!isDraft} />
            <span className="block text-xs text-gray-500 mt-1">
              Logo, footer and the unsubscribe link are added automatically.
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Button text (optional)">
              <input className={inputCls} disabled={!isDraft} value={form.cta_text} maxLength={60}
                onChange={(e) => set('cta_text', e.target.value)} placeholder="Register now" />
            </Field>
            <Field label="Button link">
              <input className={inputCls} disabled={!isDraft} value={form.cta_url} type="url"
                onChange={(e) => set('cta_url', e.target.value)} placeholder="https://sdg.unswzoo.com/…" />
            </Field>
          </div>

          {isDraft && (
            <div className="border-t border-gray-200 pt-5 space-y-4">
              <ol className="space-y-1.5 text-sm">
                <li className={campaign && !dirty ? 'text-green-700' : 'text-gray-500'}>
                  {campaign && !dirty ? '✓' : '1.'} Save the draft
                </li>
                <li className={campaign?.is_tested && !dirty ? 'text-green-700' : 'text-gray-500'}>
                  {campaign?.is_tested && !dirty ? '✓' : '2.'} Send a test to your inbox and check it
                </li>
                <li className="text-gray-500">3. Send to {overview.subscriber_count} subscribers</li>
              </ol>

              <label className="block">
                <span className="block text-sm font-medium text-gray-700 mb-1">Send test to</span>
                <input
                  type="email"
                  className={inputCls}
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="you@example.com"
                />
                <span className="block text-xs text-gray-500 mt-1">
                  Use a real inbox you can open. Each test uses one email from the daily sending quota.
                </span>
              </label>

              <div className="flex flex-wrap gap-3">
                <button onClick={save} disabled={busy !== null || !dirty}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40">
                  {busy === 'save' ? 'Saving…' : 'Save draft'}
                </button>
                <button onClick={sendTest} disabled={busy !== null || !form.subject.trim() || !form.body_html.trim() || !testEmailValid}
                  className="px-4 py-2 text-sm font-medium text-blue-700 border border-blue-300 bg-blue-50 rounded-lg hover:bg-blue-100 disabled:opacity-40">
                  {busy === 'test' ? 'Sending test…' : 'Send test to me'}
                </button>
                <button onClick={() => setShowConfirm(true)} disabled={!canSend || busy !== null}
                  title={blockers.join(' · ')}
                  className="ml-auto px-4 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed">
                  Send to subscribers…
                </button>
              </div>
              {blockers.length > 0 && (
                <p className="text-xs text-gray-500">To send: {blockers.join(' · ')}</p>
              )}
            </div>
          )}
        </div>

        {/* ------------------------------------------------ Preview */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Preview</h2>
              <p className="text-xs text-gray-500">Rendered exactly as subscribers will receive it.</p>
            </div>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
              {(['desktop', 'mobile'] as const).map((m) => (
                <button key={m} type="button" onClick={() => setPreviewMode(m)}
                  className={`px-3 py-1.5 capitalize ${previewMode === m ? 'bg-gray-900 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>

          {/* Inbox-style header */}
          <div className="rounded-t-lg border border-b-0 border-gray-200 bg-gray-50 px-4 py-3 text-sm">
            <p className="text-gray-500 truncate">From: {overview.from_email}</p>
            <p className="font-semibold text-gray-900 truncate">{form.subject || '(no subject)'}</p>
            {form.preheader && <p className="text-gray-500 truncate">{form.preheader}</p>}
          </div>
          <div className="flex-1 flex justify-center rounded-b-lg border border-gray-200 bg-gray-100 overflow-hidden">
            <iframe
              title="Email preview"
              sandbox=""
              srcDoc={previewHtml}
              className="bg-white transition-all duration-300 border-x border-gray-200"
              style={{ width: previewMode === 'mobile' ? 375 : '100%', minHeight: 720, height: '100%' }}
            />
          </div>
        </div>
      </div>

      {showConfirm && campaign && (
        <SendConfirmModal
          campaign={campaign}
          overview={overview}
          onClose={() => setShowConfirm(false)}
          onConfirm={confirmSend}
        />
      )}
    </div>
  );
};

export default CampaignEditor;
